// src/pages/Contact.jsx
import React, { useState } from "react";
import emailjs from "@emailjs/browser";
import { motion } from "framer-motion";
import { FaWhatsapp, FaPhoneAlt, FaEnvelope } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";

const Contact = () => {
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    name: "",
    whatsapp: "",
    email: "",
    message: "",
  });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setFormData((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (sending) return;
    setSending(true);

    // Map fields explicitly to the template variables you used in EmailJS
    const templateParams = {
      name: formData.name || "—",
      whatsapp: formData.whatsapp || "—",
      email: formData.email || "—",
      message: formData.message || "—",
      // you can include extra helpful fields if your template expects them:
      // reply_to: formData.email,
      // phone: formData.whatsapp
    };

    try {
      await emailjs.send(
        "service_njggcea", // replace if different
        "template_weppegr", // replace if different
        templateParams,
        "1oiA5dWUUF89BXhfm" // public key
      );

      setSent(true);
      setFormData({ name: "", whatsapp: "", email: "", message: "" });
      // hide after few seconds
      setTimeout(() => setSent(false), 4000);
    } catch (err) {
      console.error("Email failed:", err);
      setError(t("contactPage.sendError") || "Failed to send message. Try again later.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      dir={i18n.language === "ur" ? "rtl" : "ltr"}
      className="relative min-h-screen bg-gradient-to-br from-[#000814] via-[#001d3d] to-[#003566] text-white px-6 py-16 flex flex-col items-center"
    >
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="w-full max-w-6xl mx-auto"
      >
        {/* Heading */}
        <h1 className="text-4xl font-bold text-center mb-10">
          {t("contactPage.heading")}
        </h1>

        {/* --- Contact Info Row --- */}
        <div className="grid sm:grid-cols-3 gap-6 mb-16 text-center">
          <div className="bg-[#001d3d]/70 backdrop-blur-md rounded-2xl p-6 shadow-md hover:shadow-blue-500/30 transition">
            <FaPhoneAlt size={28} className="mx-auto text-blue-400 mb-3" />
            <h3 className="text-xl font-semibold mb-2">{t("contactPage.call")}</h3>
            <a href="tel:+923158182288" className="text-gray-300 hover:text-blue-400">
              03158182288
            </a>
          </div>

          <div className="bg-[#001d3d]/70 backdrop-blur-md rounded-2xl p-6 shadow-md hover:shadow-green-500/30 transition">
            <FaWhatsapp size={28} className="mx-auto text-green-400 mb-3" />
            <h3 className="text-xl font-semibold mb-2">{t("contactPage.whatsapp")}</h3>
            <a
              href="https://wa.me/923158182288"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-300 hover:text-green-400"
            >
              03158182288
            </a>
          </div>

          <div className="bg-[#001d3d]/70 backdrop-blur-md rounded-2xl p-6 shadow-md hover:shadow-sky-500/30 transition">
            <FaEnvelope size={28} className="mx-auto text-sky-400 mb-3" />
            <h3 className="text-xl font-semibold mb-2">{t("contactPage.email")}</h3>
            <a href="mailto:rawangi001@gmail.com" className="text-gray-300 hover:text-sky-400">
              rawangi001@gmail.com
            </a>
          </div>
        </div>

        {/* --- Map + Address Section --- */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          <div className="rounded-2xl overflow-hidden shadow-lg">
            <iframe
              title="Rawangi Office Location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3348.4713489246888!2d73.72025547631814!3d32.93856287359652!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x391f9125cc0b5d93%3A0xccd83bb07ac151cf!2sRawangi%20Travel%20and%20Tours!5e0!3m2!1sen!2s!4v1761373433947!5m2!1sen!2s"
              width="100%"
              height="320"
              allowFullScreen=""
              loading="lazy"
              className="border-none"
            />
          </div>

          <div className="flex flex-col justify-center bg-[#001d3d]/70 backdrop-blur-md rounded-2xl p-6 shadow-lg">
            <h3 className="text-2xl font-semibold mb-4">{t("contactPage.officeHeading")}</h3>
            <p className="text-gray-300 mb-2 font-medium">📍 {t("contactPage.officeLandmark")}</p>
            <p className="text-gray-400 mb-2">{t("contactPage.officeStreet")}</p>
            <p className="text-gray-400 mb-4">{t("contactPage.officeCity")}</p>
            <p className="text-gray-300">📞 03158182288</p>
            <p className="text-gray-300">✉️ rawangi001@gmail.com</p>
          </div>
        </div>

        {/* --- Contact Form --- */}
        <form
          onSubmit={handleSubmit}
          className="bg-[#001d3d]/70 backdrop-blur-lg p-8 rounded-2xl shadow-lg w-full max-w-6xl mx-auto space-y-5"
        >
          <div className="grid md:grid-cols-2 gap-4">
            <input
              type="text"
              name="name"
              placeholder={t("contactPage.namePlaceholder")}
              value={formData.name}
              onChange={handleChange}
              required
              className="p-3 rounded-md w-full bg-[#001933] text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-400 outline-none"
            />
            <input
              type="text"
              name="whatsapp"
              placeholder={t("contactPage.whatsappPlaceholder")}
              value={formData.whatsapp}
              onChange={handleChange}
              required
              className="p-3 rounded-md w-full bg-[#001933] text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-400 outline-none"
            />
          </div>

          <input
            type="email"
            name="email"
            placeholder={t("contactPage.emailPlaceholder")}
            value={formData.email}
            onChange={handleChange}
            required
            className="p-3 rounded-md w-full bg-[#001933] text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-400 outline-none"
          />

          <textarea
            name="message"
            placeholder={t("contactPage.messagePlaceholder")}
            rows="4"
            value={formData.message}
            onChange={handleChange}
            required
            className="p-3 rounded-md w-full bg-[#001933] text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-400 outline-none"
          ></textarea>

          <button
            type="submit"
            disabled={sending}
            className={`px-6 py-3 rounded-lg text-lg font-semibold w-full transition duration-300 ${
              sending ? "bg-blue-400/70 cursor-not-allowed" : "bg-blue-500 hover:bg-blue-600"
            }`}
          >
            {sending ? t("contactPage.sending") || "Sending..." : t("contactPage.sendButton")}
          </button>

          {sent && (
            <p className="text-green-400 font-medium mt-2">
              {t("contactPage.sentMessage")}
            </p>
          )}

          {error && (
            <p className="text-rose-400 font-medium mt-2">
              {error}
            </p>
          )}
        </form>
      </motion.div>

      {/* Floating WhatsApp Button */}
      <a
        href="https://wa.me/923158182288"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-lg transition-transform transform hover:scale-110 z-50"
        aria-label={t("contactPage.whatsapp")}
      >
        <FaWhatsapp size={26} />
      </a>
    </section>
  );
};

export default Contact;
